import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, filter, switchMap, take, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { EnvironmentService } from '../services/environment.service';
import { AuthService, AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../services/auth.service';
import { TokenRefreshCoordinator } from '../services/token-refresh-coordinator.service';
import { AppNavigationService } from '../services/app-navigation.service';
import { PreviewModeService } from '../services/preview-mode.service';

/**
 * Attaches a Bearer Authorization header to outgoing requests targeting the application API.
 * Requests to external URLs (e.g. Keycloak token endpoint) are forwarded unchanged.
 *
 * On a 401 response from the EP API the interceptor will:
 * 1. Attempt a silent token refresh via {@link AuthService.silentRefresh}.
 * 2. Retry the original request with the new token on success.
 * 3. Navigate to `/login?expired=true` and re-throw the error on failure.
 *
 * Concurrent 401s while a refresh is already in flight are queued and unblocked once the
 * refresh resolves, ensuring only one refresh call is ever made at a time.
 */
/** Navigates to /login?expired=true, or falls back to /access-id when CustomerStore has no customerId (admin portal flow). */
function navigateToExpiredLogin(appNav: AppNavigationService, router: Router): void {
  try {
    void appNav.navigate('/login', { queryParams: { expired: 'true' } }); // NOSONAR
  } catch {
    void router.navigate(['/access-id']);
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const env         = inject(EnvironmentService);
  const authSvc     = inject(AuthService);
  const coordinator = inject(TokenRefreshCoordinator);
  const appNav      = inject(AppNavigationService);
  const router      = inject(Router);
  const previewMode = inject(PreviewModeService);

  // DEV-ONLY: policy-management requests go through the /pm-api proxy and carry
  // the policy-management access token (stored under AUTH_TOKEN_KEY by the dev
  // login). Attach it directly and keep credentials so the httpOnly refresh
  // cookie flows. The EP-API 401/refresh recovery below is EP-specific and must
  // not run for these requests.
  //
  // ADO 149676 (U6), design.md §4.3 — if the CALLER already set an Authorization
  // header, it is never overwritten here. Ethel's own services set the EP-issued
  // token explicitly so policy-management authenticates the request on the
  // `EmployeePortal` scheme, not `Bearer`/Console — every OTHER /pm-api consumer is
  // unaffected because none of them sets the header themselves.
  const pmBase = env.policyManagementApiUrl;
  if (pmBase && req.url.startsWith(pmBase)) {
    if (req.headers.has('Authorization')) {
      return next(req.clone({ withCredentials: true }));
    }
    const pmToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
    const pmReq = pmToken
      ? req.clone({ setHeaders: { Authorization: `Bearer ${pmToken}` }, withCredentials: true })
      : req.clone({ withCredentials: true });
    return next(pmReq);
  }

  // Pass through requests that are not targeting the API (e.g. Keycloak, CDN).
  // Also pass through when employeePortalApiUrl is empty (proxy mode / test env) to avoid
  // matching every request with ''.startsWith('') === true.
  const isApiRequest =
    (env.employeePortalApiUrl && req.url.startsWith(env.employeePortalApiUrl)) ||
    (env.myCMNewApiUrl && req.url.startsWith(env.myCMNewApiUrl));

  if (!isApiRequest) {
    return next(req);
  }

  // Preview mode: no auth token exists and API calls are not expected to succeed.
  // Pass the request through without a Bearer header and without triggering a
  // login redirect on 401 — the preview tab has no session to expire.
  if (previewMode.isPreview()) {
    return next(req);
  }

  const token   = sessionStorage.getItem(AUTH_TOKEN_KEY);
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // DEV-ONLY: in offline bypass we authenticate against policy-management, so
      // 401s from the EP API (whose token we don't hold) are EXPECTED and must NOT
      // trigger the EP silent-refresh → expired-logout flow (which bounces the user
      // to /login?expired=true). The PM token is kept alive by AuthService's
      // proactive timer instead. Surface the error to the caller and move on.
      if (env.devAuthBypass) {
        return throwError(() => error);
      }

      // Only attempt recovery for 401s; never retry the refresh endpoint itself,
      // and never intercept 401s from auth endpoints (e.g. login with wrong credentials).
      const isRefreshUrl = req.url.includes('/auth/refresh');
      const isAuthEndpoint = req.url.includes('/auth/login') || req.url.includes('/auth/sso/callback');
      if (error.status !== 401 || isRefreshUrl || isAuthEndpoint) {
        return throwError(() => error);
      }

      // A refresh is already in flight — queue this request behind it.
      if (coordinator.isRefreshing) {
        return coordinator.refreshTokenSubject.pipe(
          filter((t): t is string => t !== null),
          take(1),
          switchMap(newToken =>
            next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
          )
        );
      }

      // No refresh token means there is no session to renew and none to expire — this is an
      // ordinary unauthenticated request, not a timed-out one. Rethrow and let the route guards
      // decide where the visitor goes; do NOT navigate to `?expired=true`, which would tell a
      // first-time visitor their session expired due to inactivity when they never had one.
      //
      // Checked here, after the in-flight branch above: if a refresh cycle is already running then
      // a session demonstrably exists and the request should still queue behind it.
      //
      // This surfaces on the login-required form link. `authGuard` and `portalConfigGuard` sit in
      // the same `canActivate` array and Angular runs them concurrently, so the authenticated
      // portal-config request fires while `authGuard` is still deciding, and whichever navigation
      // resolves first wins. The misleading banner was therefore already reachable — it just
      // became deterministic once `authGuard` gained an await for the session hand-off.
      if (!sessionStorage.getItem(REFRESH_TOKEN_KEY)) {
        return throwError(() => error);
      }

      // Start a new refresh cycle.
      coordinator.isRefreshing = true;
      coordinator.refreshTokenSubject.next(null);

      return authSvc.silentRefresh().pipe(
        switchMap(success => {
          coordinator.isRefreshing = false;
          if (success) {
            const newToken = sessionStorage.getItem(AUTH_TOKEN_KEY) ?? '';
            coordinator.refreshTokenSubject.next(newToken);
            return next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
          }
          navigateToExpiredLogin(appNav, router);
          return throwError(() => error);
        }),
        catchError(err => {
          coordinator.isRefreshing = false;
          navigateToExpiredLogin(appNav, router);
          return throwError(() => err);
        })
      );
    })
  );
};
